import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import type { RootState } from '../../store'
import { Spinner } from 'tamagui'
import { CobuStack, CobuButton, CobuText, CobuHeader, CobuBackButton } from '@cobu/ui'
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { useChannelList } from '../../hooks/useChannelList'
import { Strings } from '../../constants'
import { ChannelPreviewMessenger, ComponentProvider } from 'stream-chat-react'

export default function Channels() {
  const strings = Strings.get()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const userId = useSelector((s: RootState) => s.auth.user?.uuid as string | undefined)

  const { chatClient, isLoading, error, isStreamAvailable, Chat, ChannelList, i18nInstance, refreshChannels } = useChannelList({ enabled: true })

  // Auto-navigate to a DM if userId is provided
  useEffect(() => {
    const targetUserId = params.get('userId')
    if (!targetUserId || !chatClient || !userId) return
    ;(async () => {
      try {
        const all = await chatClient.queryChannels({ type: 'messaging', members: { $in: [userId, targetUserId] } }, { last_message_at: -1 }, { limit: 1 })
        const channel = all?.[0]
        if (channel) {
          navigate(`/chat/${channel.cid || channel.id}`)
        }
      } catch (e) {
        // ignore and stay on list
      }
    })()
  }, [params, chatClient, userId, navigate])

  const handleSelect = useCallback((arg: any) => {
    const ch = arg?.cid || arg?.id ? arg : arg?.channel
    const target = ch?.cid || ch?.id
    if (target) navigate(`/chat/${target}`)
  }, [navigate])

  if (!isStreamAvailable) {
    return (
      <CobuStack>
        <CobuHeader
          title={strings.chat.channelList.title}
          renderLeft={() => <CobuBackButton onPress={() => navigate('/chat')} />}
        />
        <CobuStack padding="$4" minHeight="60vh" display="flex" flexDirection="column" ai="center" jc="center">
          <CobuText color="$colorMuted" ta="center" lineHeight={20}>
            {strings.chat.channelList.chatNotAvailable}
          </CobuText>
        </CobuStack>
      </CobuStack>
    )
  }

  if (isLoading) {
    return (
      <CobuStack>
        <CobuHeader
          title={strings.chat.channelList.title}
          renderLeft={() => <CobuBackButton onPress={() => navigate('/chat')} />}
        />
        <CobuStack padding="$4" display="flex" ai="center" jc="center">
          <Spinner size="large" />
          <CobuStack height="$2" />
          <CobuText>{strings.chat.channelList.loading}</CobuText>
        </CobuStack>
      </CobuStack>
    )
  }

  if (error) {
    return null
  }

  if (!chatClient || !Chat || !ChannelList) {
    return (
      <CobuStack padding="$4" display="flex" ai="center" jc="center">
        <CobuText fontWeight="700" fontSize={'$6'}>{strings.chat.channelList.title}</CobuText>
        <CobuStack height="$2" />
        <CobuText color="$colorMuted">{strings.chat.channelList.chatNotAvailable}</CobuText>
      </CobuStack>
    )
  }

  return (
    <CobuStack>
      <CobuHeader
        title={strings.chat.channelList.title}
        renderLeft={() => <CobuBackButton onPress={() => navigate('/chat')} />}
        renderRight={() => (
          <CobuButton backgroundColor="transparent" size="$4" onPress={() => navigate('/chat/all-members', { state: { enableDM: true, currentUserId: userId } })}>
            <CreateOutlinedIcon fontSize="medium" htmlColor="#ffffff" />
          </CobuButton>
        )}
      />
      <Chat client={chatClient} {...(i18nInstance ? { i18nInstance } : {})}>
        <ComponentProvider value={{ ChannelPreviewActionButtons: () => null }}>
          <CobuStack w="100%">
            <ChannelList
              onSelect={handleSelect}
              filters={{ type: 'messaging', members: { $in: [userId] } }}
              sort={{ last_message_at: -1 }}
              options={{ state: true, watch: true, presence: true }}
              Preview={(p: any) => (
                <ChannelPreviewMessenger
                  {...p}
                  onSelect={() => handleSelect(p.channel)}
                />
              )}
            />
          </CobuStack>
        </ComponentProvider>
      </Chat>
    </CobuStack>
  )
}


